import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { useToast } from "../../context/ToastContext";
import OrganizationSelect from "./components/OrganizationSelect/OrganizationSelect";
import { ChevronDown } from "lucide-react";

// Badge handles case‑insensitive status (ACTIVE → Active)
const Badge = ({ value }) => {
  const isActive = value?.toLowerCase() === "active";
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isActive
        ? "bg-emerald-50 text-emerald-700"
        : "bg-amber-50 text-amber-700"
        }`}
    >
      {isActive ? "Active" : "Inactive"}
    </span>
  );
};

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState("");
  const [range, setRange] = useState("Last 30 days");
  const [loading, setLoading] = useState(false);
  const { success, error } = useToast();

  const [isRangeOpen, setIsRangeOpen] = useState(false);


  // Convert range label to start_date & end_date (YYYY-MM-DD)
  const getDateRange = (rangeLabel) => {
    const now = new Date();
    let startDate, endDate = now;

    switch (rangeLabel) {
      case "Today":
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;
      case "Last 7 days":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "Last 30 days":
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
        break;
      case "This month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 30);
    }

    const formatDate = (d) => d.toISOString().split("T")[0];
    return {
      start_date: formatDate(startDate),
      end_date: formatDate(endDate),
    };
  };

  const fetchDashboardData = async (orgId, rangeLabel) => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange(rangeLabel);
      const params = new URLSearchParams();
      if (orgId) params.append("orgId", orgId);
      params.append("start_date", start_date);
      params.append("end_date", end_date);

      // Fetch both endpoints in parallel
      const [metricsResponse, topOrgsResponse] = await Promise.all([
        api.get(`/dashboard/metrics?${params.toString()}`),
        api.get(`/dashboard/top-organizations?${params.toString()}`),
      ]);

      if (metricsResponse?.data?.success && topOrgsResponse?.data?.success) {
        // Metrics are under 'metrics' key
        setMetrics(metricsResponse.data.metrics);
        // Top organizations are under 'data.organizations'
        setOrganizations(topOrgsResponse.data.data.organizations || []);
      } else {
        const msg =
          metricsResponse?.data?.message ||
          topOrgsResponse?.data?.message ||
          "Failed to load dashboard data";
        error(msg);
        setMetrics(null);
        setOrganizations([]);
      }
    } catch (err) {
      console.error(err);
      error(err?.response?.data?.message || "An error occurred while fetching dashboard");
      setMetrics(null);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".range-dropdown")) {
        setIsRangeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    fetchDashboardData(organization, range);
  }, [organization, range]);

  // —————— Skeleton Loader ——————
  if (loading) {
    return (
      <div className="space-y-7 animate-pulse">
        {/* Header skeleton */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="h-8 w-48 rounded bg-slate-200"></div>
            <div className="mt-2 h-4 w-64 rounded bg-slate-200"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-56 rounded bg-slate-200"></div>
            <div className="h-10 w-40 rounded bg-slate-200"></div>
          </div>
        </div>

        {/* Stats cards skeleton (4 cards) */}
        <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="h-4 w-24 rounded bg-slate-200"></div>
              <div className="mt-3 h-8 w-16 rounded bg-slate-200"></div>
              <div className="mt-2 h-3 w-32 rounded bg-slate-200"></div>
            </div>
          ))}
        </section>

        {/* Table skeleton – unchanged */}
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-5 py-4">
            <div className="h-5 w-40 rounded bg-slate-200"></div>
            <div className="mt-1 h-4 w-64 rounded bg-slate-200"></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {["Organization", "Creatives", "XML hits", "Impressions", "CTR", "Status"].map((_, idx) => (
                    <th key={idx} className="px-5 py-3">
                      <div className="h-4 w-20 rounded bg-slate-200"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[...Array(5)].map((_, rowIdx) => (
                  <tr key={rowIdx}>
                    {[...Array(6)].map((_, colIdx) => (
                      <td key={colIdx} className="px-5 py-4">
                        <div className="h-4 w-24 rounded bg-slate-200"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }

  // —————— Error / Empty state ——————
  if (!metrics) {
    return (
      <div className="py-20 text-center text-sm text-slate-500">
        Unable to load dashboard metrics. Please try again later.
      </div>
    );
  }

  // —————— Build stats from metrics (use snake_case keys) ——————
  const stats = [
    {
      label: "Total Organizations",
      value: metrics.total_organizations ?? 0,
      note: "Active organizations",
    },
    {
      label: "Active Subscriptions",
      value: metrics.active_subscriptions ?? 0,
      note: "Current subscribers",
    },
    {
      label: "Total Creatives",
      value: metrics.total_creatives ?? 0,
      note: "Across all orgs",
    },
    {
      label: "Total XML Hits",
      value: metrics.total_xml_hits ?? 0,
      note: "Total requests",
    },
  ];


  // —————— Main render ——————
  return (
    <div className="space-y-7">
      {/* Header – unchanged */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Platform overview
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Monitor customers, subscriptions, and platform usage in one place.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <OrganizationSelect
            value={organization}
            onChange={(orgId) => setOrganization(orgId)}
            className="w-[265px]"
          />

          {/* <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400"
          >
            <option>Today</option>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
            <option>This month</option>
          </select> */}

          <div className="range-dropdown relative min-w-[145px]">
            <button
              type="button"
              onClick={() => setIsRangeOpen((prev) => !prev)}
              className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5 pr-3 text-sm font-medium text-slate-700 shadow-sm outline-none transition hover:border-[#ff696855] focus:border-[#ff6968]"
            >
              <span>{range}</span>
              <ChevronDown
                size={17}
                className={`text-slate-400 transition-transform duration-200 ${isRangeOpen ? "rotate-180" : ""
                  }`}
              />
            </button>

            {isRangeOpen && (
              <div className="absolute left-0 right-0 top-full flex flex-col gap-0.5 z-50 mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                {["Today", "Last 7 days", "Last 30 days", "This month"].map(
                  (option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setRange(option);
                        setIsRangeOpen(false);
                      }}
                      className={`flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm transition ${range === option
                        ? "bg-[#FFE2DE] font-semibold text-[#e95555]"
                        : "text-slate-600 hover:bg-[#FFE2DE] hover:text-[#e95555]"
                        }`}
                    >
                      {option}
                    </button>
                  )
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Stats Cards – from metrics API, 4 columns on large screens */}
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <article
            key={stat.label}
            className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <p className="text-sm font-medium text-slate-500">{stat.label}</p>
            <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
              {stat.value}
            </p>
            <p className="mt-2 text-xs text-slate-400">{stat.note}</p>
          </article>
        ))}
      </section>

      {/* Top Organizations Table – from top-organizations API */}
      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4">
          <h3 className="font-semibold">Top organizations</h3>
          <p className="mt-1 text-sm text-slate-500">
            Ranked by platform activity and performance.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {["Organization", "Creatives", "XML hits", "Impressions", "CTR", "Status"].map((head) => (
                  <th key={head} className="px-5 py-3 font-semibold">
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {organizations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-500">
                    No organizations found.
                  </td>
                </tr>
              ) : (
                organizations.map((org) => (
                  <tr key={org.org_id} className="hover:bg-slate-50/60">
                    <td className="px-5 py-4 font-medium text-slate-900">
                      {org.org_name}
                    </td>
                    <td className="px-5 py-4">{org.creatives}</td>
                    <td className="px-5 py-4">{org.xml_hits}</td>
                    <td className="px-5 py-4">{org.impressions}</td>
                    <td className="px-5 py-4">{org.ctr}</td>
                    <td className="px-5 py-4">
                      <Badge value={org.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}